import type { QueryClient, QueryKey } from "@tanstack/query-core";
import type { Page } from "@baton/api";

/**
 * FR-015-122, `contracts/client-api.md` § Pagination and infinite scroll: cursor pagination
 * over opaque `next_cursor` values with `useInfiniteQuery`, `limit` 50 for lists and 25 for
 * feeds and never above 200, `include_total=true` only for a group header count and a bulk
 * selection summary, the two-dimensional grouped shape with per-group cursors, the sentinel
 * 600 px before the end, the first-page reset on a stale cursor, and an explicit Load more.
 * The React hook lives in the screen; everything the hook is given comes from here.
 */
export const LIST_LIMIT = 50;
export const FEED_LIMIT = 25;
export const MAX_LIMIT = 200;
/** The distance from the end of the scroll container at which the next page is requested. */
export const SENTINEL_DISTANCE_PX = 600;

export type PageKind = "list" | "feed";

export function limitFor(kind: PageKind, requested?: number): number {
  const base = requested ?? (kind === "list" ? LIST_LIMIT : FEED_LIMIT);
  return Math.min(MAX_LIMIT, Math.max(1, base));
}

/** The one reason `include_total=true` may be sent. */
export type TotalReason = "group-header" | "bulk-summary";

export function includeTotal(reason: TotalReason | null): { include_total?: true } {
  return reason === null ? {} : { include_total: true };
}

export interface CursorPageParam {
  readonly cursor: string | null;
}

/** The options `useInfiniteQuery` takes for a flat list: the first page has no cursor, and
 * every later page follows the opaque `next_cursor` the previous one returned. */
export function cursorPagination<T>() {
  return {
    initialPageParam: { cursor: null } as CursorPageParam,
    getNextPageParam: (last: Page<T>): CursorPageParam | undefined => (last.next_cursor === null ? undefined : { cursor: last.next_cursor }),
  };
}

export function flattenPages<T>(pages: readonly Page<T>[] | undefined): T[] {
  return (pages ?? []).flatMap((page) => page.items);
}

/** 001 § List query: the grouped response. */
export interface Group<T, K = unknown> {
  readonly key: K | null;
  readonly count: number;
  readonly items: readonly T[];
  readonly next_cursor: string | null;
}

export interface GroupedPage<T, K = unknown> {
  readonly groups: readonly Group<T, K>[];
  readonly next_group_cursor: string | null;
  /** 002 § Grouping: whether an item may sit in more than one group; absent on a page that says nothing. */
  readonly multi?: boolean;
  /** 002 § Filtering: the clauses the list dropped for a deleted or unreadable field. */
  readonly warnings?: readonly string[];
}

export interface GroupedPageParam {
  /** The collection's own cursor for the next set of groups. */
  readonly groupCursor: string | null;
  /** A single group's cursor for that group's Load more, with the group it belongs to. */
  readonly group?: { readonly keyJson: string; readonly cursor: string };
}

/** The two-dimensional shape: pages are stored under one list key, and a page either adds
 * groups (the collection's Load more) or extends one group (that group's Load more). */
export function groupedPagination<T, K>() {
  return {
    initialPageParam: { groupCursor: null } as GroupedPageParam,
    getNextPageParam: (last: GroupedPage<T, K>): GroupedPageParam | undefined =>
      last.next_group_cursor === null ? undefined : { groupCursor: last.next_group_cursor },
  };
}

export function groupKeyJson(key: unknown): string {
  return JSON.stringify(key ?? null);
}

/**
 * The rows of a page set, first occurrence per id. A cursor is opaque and a page is only
 * guaranteed not to repeat what the same cursor already answered, so a reader that merges several
 * cursors (a group's own and the collection's, a window's rows and its bands) keeps each row once.
 */
export function dedupeRows<T extends { readonly id: string }>(rows: readonly T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

/** Merge every stored page into one ordered set of groups, each carrying its own cursor. */
export function mergeGroups<T, K>(pages: readonly GroupedPage<T, K>[] | undefined): Group<T, K>[] {
  const order: string[] = [];
  const byKey = new Map<string, Group<T, K>>();
  for (const page of pages ?? []) {
    for (const group of page.groups) {
      const id = groupKeyJson(group.key);
      const existing = byKey.get(id);
      if (existing) {
        byKey.set(id, { ...existing, items: [...existing.items, ...group.items], next_cursor: group.next_cursor, count: group.count });
      } else {
        order.push(id);
        byKey.set(id, group);
      }
    }
  }
  return order.flatMap((id) => {
    const group = byKey.get(id);
    return group === undefined ? [] : [group];
  });
}

/** The page parameter for one group's Load more. */
export function nextGroupPage<T, K>(group: Group<T, K>, groupCursor: string | null): GroupedPageParam | null {
  return group.next_cursor === null ? null : { groupCursor, group: { keyJson: groupKeyJson(group.key), cursor: group.next_cursor } };
}

/** A `bad_request` on a cursor resets the query to its first page and refetches (§ Pagination). */
export async function resetToFirstPage(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
  queryClient.setQueryData(queryKey, (data: { pages: unknown[]; pageParams: unknown[] } | undefined) =>
    data === undefined ? undefined : { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) },
  );
  await queryClient.invalidateQueries({ queryKey });
}
